import { SearchInput } from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CategoriaReadingDtoProps } from "api/DTOs/CategoriaReadingDto"
import { COLOR_PRIMARY } from "common/utils/theme"
import { MODES_DOCUMENTOS } from "features/Crear/pages/FormCreacionDocumento/constant"
import { AnimatePresence, motion } from "framer-motion"
import debounce from "lodash/debounce"
import { Button } from "primereact/button"
import { InputNumber } from "primereact/inputnumber"
import { Slider } from "primereact/slider"
import { TreeSelect } from "primereact/treeselect"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FiltradoArticulosDto } from "../../SelectedArticlesDocument"

interface ReplaceKeys {
  id: string
  nombre: string
  subCategorias: string
}

const REPLACE_KEYS: ReplaceKeys = {
  id: "key",
  nombre: "label",
  subCategorias: "children",
}

const variantsFilters = {
  initial: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
}

const ArticulosFilterDocumento = ({
  showDisplayModes,
  categorias,
  onFilter,
  values,
  setFieldValue,
}: {
  showDisplayModes: boolean
  categorias: CategoriaReadingDtoProps[]
  onFilter: (event: FiltradoArticulosDto) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: $TSMigrationFixMe
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: $TSMigrationFixMe,
    shouldValidate?: boolean
  ) => Promise<void>
}) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const categoriasIdCached = useRef<any>([])
  const precioLowerLimitCached = useRef(0)
  const precioUpperLimitCached = useRef(10000)
  const searchConceptoCached = useRef("")
  const skipCount = useRef(0)

  const [showFilters, setShowFilters] = useState(false)
  const [searchConcepto, setSearchConcepto] = useState("")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [categoriasId, setCategoriasId] = useState<any>([])
  const [precioLowerLimit, setPrecioLowerLimit] = useState(0)
  const [precioUpperLimit, setPrecioUpperLimit] = useState(10000)
  const [preciosRange, setPreciosRange] = useState<[number, number]>([0, 10000])

  const categoriasOptions = useMemo(
    () =>
      categorias?.map((categoria: CategoriaReadingDtoProps) => {
        const replaceKeys = (obj: CategoriaReadingDtoProps) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result: $TSMigrationFixMe = {}
          Object.keys(obj).forEach((key) => {
            const newKey = REPLACE_KEYS[key as keyof ReplaceKeys] || key
            const value = obj[key as keyof CategoriaReadingDtoProps]

            if (Array.isArray(value)) {
              result[newKey] = value.map((child: CategoriaReadingDtoProps) =>
                replaceKeys(child)
              )
            } else {
              result[newKey] = value
            }
          })
          return result
        }
        return replaceKeys(categoria)
      }),
    [categorias]
  )

  useEffect(() => {
    setPreciosRange([precioLowerLimit, precioUpperLimit])
  }, [precioLowerLimit, precioUpperLimit])

  const handleOnFilter = useMemo(
    () =>
      debounce(({ withSkipCount = false } = {}) => {
        if (!withSkipCount) skipCount.current = 0

        return onFilter?.({
          PrecioVentaLowerLimit: precioLowerLimitCached.current,
          PrecioVentaUpperLimit: precioUpperLimitCached.current,
          CategoriasId: categoriasIdCached.current || [],
          Concepto: searchConceptoCached.current,
          SkipCount: skipCount.current,
          MaxResultCount: 10,
          EsDeCompra: values.documentoType.mode === MODES_DOCUMENTOS.COMPRA,
        })
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFilter]
  )

  useEffect(() => {
    handleOnFilter()
  }, [handleOnFilter])

  const handleOnSelectCategoria = ({
    target: { value },
  }: $TSMigrationFixMe) => {
    categoriasIdCached.current = Object.keys(value)

    setCategoriasId(value)
    handleOnFilter()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangePrecioRange = ({ value }: $TSMigrationFixMe) => {
    const [min, max] = value

    setPreciosRange(value)
    setPrecioLowerLimit(min)
    setPrecioUpperLimit(max)

    precioLowerLimitCached.current = min
    precioUpperLimitCached.current = max

    handleOnFilter()
  }

  const handleOnChangeLowerPrecioRange = ({ value }: { value: number }) => {
    precioLowerLimitCached.current = value
    setPrecioLowerLimit(value)
    setPreciosRange([value, preciosRange?.[1]])

    handleOnFilter()
  }

  const handleOnChangeHigherPrecioRange = ({ value }: { value: number }) => {
    precioUpperLimitCached.current = value
    setPrecioUpperLimit(value)
    setPreciosRange([preciosRange?.[0], value])

    handleOnFilter()
  }

  const handleResetFilters = () => {
    categoriasIdCached.current = []
    precioLowerLimitCached.current = 0
    precioUpperLimitCached.current = 10000
    searchConceptoCached.current = ""

    setCategoriasId([])
    setPrecioLowerLimit(0)
    setPrecioUpperLimit(10000)
    setSearchConcepto("")

    handleOnFilter()
  }

  const handleOnChangeConcepto = (search: string) => {
    setSearchConcepto(search)
    searchConceptoCached.current = search
    handleOnFilter()
  }

  return (
    <>
      <div className='tw-mt-px'>
        <div
          className={`tw-w-full tw-flex tw-flex-row ${
            values.documentoType.mode === MODES_DOCUMENTOS.COMPRA
              ? "tw-justify-between"
              : "tw-justify-end"
          } tw-items-center tw-h-[72px] tw-px-6 tw-bg-simple-10 tw-shadow`}
        >
          {values.documentoType.mode === MODES_DOCUMENTOS.COMPRA && (
            <div className='tw-w-64 tw-h-12 tw-p-1 tw-grid tw-grid-cols-2 tw-gap-1 tw-rounded tw-bg-neutral-10'>
              <div
                className={`tw-h-full ${
                  values.config.esCompraArticulos
                    ? "tw-bg-white tw-text-text"
                    : "tw-text-neutral-75 hover:!tw-shadow tw-cursor-pointer"
                } tw-flex tw-flex-row tw-justify-center tw-items-center tw-rounded`}
                onClick={() =>
                  !values.config.esCompraArticulos &&
                  setFieldValue("config.esCompraArticulos", true)
                }
              >
                {t("catalogos.idView.filter.proveedor")}
              </div>
              <div
                className={`tw-h-full ${
                  !values.config.esCompraArticulos
                    ? "tw-bg-white tw-text-text"
                    : "tw-text-neutral-75 hover:!tw-shadow tw-cursor-pointer"
                } tw-flex tw-flex-row tw-justify-center tw-items-center tw-rounded`}
                onClick={() =>
                  values.config.esCompraArticulos &&
                  setFieldValue("config.esCompraArticulos", false)
                }
              >
                {t("catalogos.idView.filter.misArticulos")}
              </div>
            </div>
          )}
          <div className='tw-flex tw-flex-row tw-items-center tw-w-fit tw-gap-3'>
            <SearchInput
              placeholder={`${t("catalogos.idView.filter.placeholderSearch")}`}
              onChange={(ev: string) => {
                handleOnChangeConcepto(ev)
              }}
              inputValue={searchConcepto}
              className='tw-h-10 sm:tw-w-72'
            />

            <div
              className='tw-w-10 tw-h-10 tw-flex tw-justify-center tw-items-center tw-shadow tw-cursor-pointer hover:!tw-shadow-md'
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={["fal", "bars-filter"]} />
            </div>
            {showDisplayModes && (
              <>
                <div className='tw-w-24 tw-h-12 tw-flex tw-flex-row tw-rounded tw-shadow'>
                  <div
                    className={`tw-w-12 tw-h-full ${
                      values.config.gridArticles
                        ? "tw-bg-secondary tw-text-white"
                        : "hover:!tw-bg-neutral-10 tw-text-neutral-75 hover:!tw-shadow tw-cursor-pointer"
                    } tw-flex tw-flex-row tw-justify-center tw-items-center tw-rounded-l`}
                    onClick={() =>
                      !values.config.gridArticles &&
                      setFieldValue("config.gridArticles", true)
                    }
                  >
                    <FontAwesomeIcon icon={["fal", "grid-2"]} />
                  </div>
                  <div
                    className={`tw-w-12 tw-h-full ${
                      !values.config.gridArticles
                        ? "tw-bg-secondary tw-text-white"
                        : "hover:!tw-bg-neutral-10 tw-text-neutral-75 hover:!tw-shadow tw-cursor-pointer"
                    } tw-flex tw-flex-row tw-justify-center tw-items-center tw-rounded-r`}
                    onClick={() =>
                      values.config.gridArticles &&
                      setFieldValue("config.gridArticles", false)
                    }
                  >
                    <FontAwesomeIcon icon={["fal", "list"]} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showFilters && (
          <motion.div
            variants={variantsFilters}
            initial='initial'
            animate='visible'
            exit='exit'
            className='mx-0 container-filters row'
          >
            <div className='p-col-12 p-fluid p-formgrid p-grid'>
              <div className='mt-3 mt-lg-0 p-col-12 col-lg-3'>
                <TreeSelect
                  className='text-left'
                  value={categoriasId}
                  options={categoriasOptions}
                  onChange={handleOnSelectCategoria}
                  display='chip'
                  selectionMode='checkbox'
                  placeholder={`${t(
                    "catalogos.idView.filter.placeholderCategory"
                  )}`}
                  filter
                />
              </div>

              <div className='mt-3 mt-lg-0 p-col-12 col-lg-2'>
                <InputNumber
                  value={precioLowerLimit}
                  onChange={handleOnChangeLowerPrecioRange}
                  minFractionDigits={2}
                  mode='currency'
                  currency={values?.divisa || "USD"} // Provide a default currency code
                />
              </div>

              <div className='mt-3 mt-lg-0 p-col-12 col-lg-3 d-flex align-items-center'>
                <Slider
                  className='w-100'
                  value={preciosRange}
                  onChange={handleChangePrecioRange}
                  step={0.5}
                  min={0}
                  max={10000}
                  range
                />
              </div>

              <div className='mt-3 mt-lg-0 p-col-12 col-lg-2'>
                <InputNumber
                  value={precioUpperLimit}
                  onChange={handleOnChangeHigherPrecioRange}
                  minFractionDigits={2}
                  mode='currency'
                  currency={values?.divisa || "USD"} // Provide a default currency code
                />
              </div>

              <div className='p-col-12 col-lg-2'>
                <Button
                  className='p-button-text f-weight-500 hover:!tw-shadow-md !tw-rounded'
                  style={{ color: COLOR_PRIMARY }}
                  label={`${t("catalogos.idView.filter.labelClear")}`}
                  onClick={handleResetFilters}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default ArticulosFilterDocumento
